// @ts-nocheck
import * as React from 'react';
import { useEffect, useState } from 'react';
import { Icon, LiveDemoTopBar, Stepper } from '../components/LiveDemoChrome';
import { AGE_GROUPS, GRADES, QUESTIONS } from '../data/liveDemoData';
import { useLiveDemoSession } from '../hooks/useLiveDemoSession';
import { ageGroupFor, computeProfile, meterColor } from './liveDemoHelpers';

export function Intake({ data, setData, onProceed }) {
  const upd = (key) => (event) => setData({ ...data, [key]: event.target.value });
  const group = ageGroupFor(data.age);
  const ready = data.studentName && data.age && data.gender && data.grade && data.whatsapp && data.primaryParent;

  return (
    <div className="screen">
      <LiveDemoTopBar now="Student & Parent" />
      <Stepper active={1} />
      <div className="scroll"><div className="wrap">
        <span className="eyebrow"><Icon name="sparkles" /> Intelligence capture</span>
        <h1 className="title">Let's get to know <span className="accent">the child.</span></h1>
        <p className="lead">A short, guided conversation - not a form. Everything captured here personalises the live demo and feeds the school's faculty system.</p>

        <div className="intake-grid">
          <div className="card intake-card">
            <div className="assist-line">
              <div className="sp"><Icon name="sparkles" /></div>
              <p>I'll adapt the next questions to the child's <b>age and grade</b>. Start with their name - the rest follows naturally.</p>
            </div>

            <div className="subgroup-title"><Icon name="student" style={{ width: 16, height: 16 }} /> Student <span className="ln" /></div>
            <div className="frow">
              <div className="field full">
                <label>Child's name</label>
                <input value={data.studentName} onChange={upd("studentName")} placeholder="e.g. Aarav Reddy" />
              </div>
              <div className="field">
                <label>Age</label>
                <input type="number" min="2" max="18" value={data.age} onChange={upd("age")} placeholder="Years" />
              </div>
              <div className="field">
                <label>Current grade</label>
                <select value={data.grade} onChange={upd("grade")}>
                  <option value="">Select…</option>
                  {GRADES.map((grade) => <option key={grade} value={grade}>{grade}</option>)}
                </select>
              </div>
              <div className="field full">
                <label>Gender</label>
                <div className="chips">
                  {["Girl", "Boy", "Prefer not to say"].map((gender) => (
                    <div key={gender} className={"chip" + (data.gender === gender ? " sel" : "")} onClick={() => setData({ ...data, gender })}>{gender}</div>
                  ))}
                </div>
              </div>
              <div className="field full">
                <label>Target admission grade</label>
                <div className="chips">
                  {GRADES.map((grade) => (
                    <div key={grade} className={"chip" + (data.target === grade ? " sel" : "")} onClick={() => setData({ ...data, target: grade })}>{grade}</div>
                  ))}
                </div>
              </div>
            </div>

            <div className="subgroup-title"><Icon name="parent" style={{ width: 16, height: 16 }} /> Parent <span className="ln" /></div>
            <div className="frow">
              <div className="field">
                <label>Primary parent name</label>
                <input value={data.primaryParent} onChange={upd("primaryParent")} placeholder="Who we'll speak with" />
              </div>
              <div className="field">
                <label>WhatsApp number</label>
                <input value={data.whatsapp} onChange={upd("whatsapp")} placeholder="+91 …" />
              </div>
              <div className="field">
                <label>Mother's name <span className="opt-tag">· optional</span></label>
                <input value={data.mother} onChange={upd("mother")} placeholder="Optional" />
              </div>
              <div className="field">
                <label>Father's name <span className="opt-tag">· optional</span></label>
                <input value={data.father} onChange={upd("father")} placeholder="Optional" />
              </div>
            </div>
          </div>

          <div className="card feed-card">
            <p className="ft">Feeds the live demo</p>
            <p className="fsub">Captured intelligence carries straight into the dashboard and faculty app - no re-entry.</p>
            <div className="feed-row"><span className="k">Student</span><span className={"v" + (data.studentName ? "" : " empty")}>{data.studentName || "Awaiting…"}</span></div>
            <div className="feed-row"><span className="k">Age · grade</span><span className={"v" + (data.age ? "" : " empty")}>{data.age ? `${data.age} yrs · ${data.grade || "-"}` : "Awaiting…"}</span></div>
            <div className="feed-row"><span className="k">Target</span><span className={"v" + (data.target ? "" : " empty")}>{data.target || "Awaiting…"}</span></div>
            <div className="feed-row"><span className="k">Parent</span><span className={"v" + (data.primaryParent ? "" : " empty")}>{data.primaryParent || "Awaiting…"}</span></div>
            <div className={"routed" + (data.age ? "" : " idle")}>
              {data.age
                ? <>Assessment routed to <b>{group.label}</b> ({group.band}) - tuned to {group.focus.toLowerCase()}.</>
                : <>Enter an age to route the right assessment set.</>}
            </div>
          </div>
        </div>

        <div className="qnav" style={{ maxWidth: 720 }}>
          <span style={{ fontSize: 13, color: "var(--fg-tertiary)" }}>{ready ? "All set - continue to the assessment." : "Fill the child's name, age, grade, gender, parent & WhatsApp."}</span>
          <button className="btn btn-primary" disabled={!ready} onClick={onProceed}>Continue to assessment <Icon name="arrow-right" /></button>
        </div>
      </div></div>
    </div>
  );
}

export function Assessment({ data, answers, setAnswers, previewGroup, setPreviewGroup, onComplete, isSubmitting, submitError }) {
  const routed = ageGroupFor(data.age);
  const activeId = previewGroup || routed.id;
  const group = AGE_GROUPS.find((item) => item.id === activeId);
  const questions = QUESTIONS[activeId];
  const [questionCursor, setQuestionCursor] = useState({ activeId, index: 0 });
  const qi = questionCursor.activeId === activeId ? questionCursor.index : 0;
  const setQi = (index) => setQuestionCursor({ activeId, index });

  const question = questions[qi];
  const key = `${activeId}:${question.id}`;
  const chosen = answers[key];
  const profile = computeProfile(activeId, answers);
  const answeredAll = questions.every((item) => answers[`${activeId}:${item.id}`] !== undefined);
  const isPreview = activeId !== routed.id;

  const pick = (index) => {
    setAnswers({ ...answers, [key]: index });
    if (qi < questions.length - 1) setTimeout(() => setQi(qi + 1), 220);
  };

  return (
    <div className="screen">
      <LiveDemoTopBar now="Assessment" />
      <Stepper active={2} />
      <div className="scroll"><div className="wrap">
        <div className="assess-head">
          <div>
            <span className="eyebrow"><Icon name="sparkles" /> Adaptive SWOT assessment</span>
            <h1 className="title" style={{ fontSize: 34 }}>{data.studentName || "This child"} · <span className="accent">{group.label}</span></h1>
            <p className="lead" style={{ fontSize: 16 }}>Five questions tuned for {group.band} ({group.grades}). Each answer builds the child's strengths-and-needs profile.</p>
          </div>
        </div>

        <div className="group-tabs">
          {AGE_GROUPS.map((item) => (
            <div key={item.id} className={"gtab" + (item.id === activeId ? " sel" : "")} onClick={() => setPreviewGroup(item.id)}>
              {item.label} <span className="band">{item.band}</span>{item.id === routed.id ? " ●" : ""}
            </div>
          ))}
        </div>
        {isPreview && <div style={{ marginBottom: 4 }}><span className="preview-note"><Icon name="shield" style={{ width: 13, height: 13 }} /> Previewing another age set · {routed.label} is auto-selected for {data.studentName || "this child"}</span></div>}

        <div className="qprogress">
          <div className="bar"><i style={{ width: `${(profile.answered / questions.length) * 100}%` }} /></div>
          <span className="lab">{profile.answered} / {questions.length} answered</span>
        </div>

        <div className="card qcard">
          <div className="qhead">
            <span className="num">Q{qi + 1}<span style={{ color: "var(--fg-tertiary)", fontWeight: 600 }}> / {questions.length}</span></span>
            <span className="dim">{question.dim}</span>
          </div>
          <div className="qtext">{question.q}</div>
          <div className="opts">
            {question.options.map((option, index) => (
              <button key={option.t} className={"opt-row" + (chosen === index ? " sel" : "")} onClick={() => pick(index)}>
                <span className="radio" />
                <span className="label">{option.t}</span>
                <span className="key">{["A", "B", "C", "D"][index]}</span>
              </button>
            ))}
          </div>
          <div className="qnav">
            <button className="btn btn-ghost" disabled={qi === 0} onClick={() => setQi(qi - 1)}>Back</button>
            {qi < questions.length - 1
              ? <button className="btn btn-ghost" disabled={chosen === undefined} onClick={() => setQi(qi + 1)}>Next question</button>
              : <button className="btn btn-primary" disabled={!answeredAll || isPreview || isSubmitting} onClick={onComplete}>
                {isSubmitting ? 'Saving lead…' : <>Build the profile <Icon name="arrow-right" /></>}
              </button>}
          </div>
          {submitError && <div className="lead-submit-error" role="alert">{submitError}</div>}
        </div>

        <div className="swot-strip">
          {profile.dims.map((dim) => (
            <div className="swot-cell" key={dim.dim} style={{ opacity: dim.present ? 1 : 0.4 }}>
              <div className="sk">{dim.dim}</div>
              <div className="meter"><i style={{ width: dim.has ? `${dim.pct}%` : "0%", background: meterColor(dim.pct) }} /></div>
            </div>
          ))}
        </div>
        {isPreview && <div className="qnav" style={{ maxWidth: 720 }}>
          <span style={{ fontSize: 13, color: "var(--fg-tertiary)" }}>Reviewing the {group.label} set. Return to {routed.label} to continue the live flow.</span>
          <button className="btn btn-primary" onClick={() => setPreviewGroup(null)}>Back to {data.studentName || "child"}'s set</button>
        </div>}
      </div></div>
    </div>
  );
}

const PREP_TASKS = [
  "Building student SWOT profile",
  "Mapping learning needs",
  "Prioritising AI teachers · Inspire 365",
  "Sequencing counselling videos & podcasts",
  "Personalising the live demo",
];

export function Prep({ data, answers, onOpen }) {
  const session = useLiveDemoSession();
  const routed = ageGroupFor(data.age);
  const profile = computeProfile(routed.id, answers);
  const [step, setStep] = useState(0);
  const [pct, setPct] = useState(0);
  const [openingDashboard, setOpeningDashboard] = useState(false);
  const done = step >= PREP_TASKS.length;

  const handleOpen = async () => {
    if (openingDashboard) return;
    setOpeningDashboard(true);
    try {
      await onOpen();
    } finally {
      setOpeningDashboard(false);
    }
  };

  useEffect(() => {
    if (step < PREP_TASKS.length) {
      const timer = setTimeout(() => setStep(step + 1), 850);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [step]);

  useEffect(() => {
    const target = done ? profile.needsMet : Math.round((step / PREP_TASKS.length) * profile.needsMet);
    const timer = setTimeout(() => setPct(target), 80);
    return () => clearTimeout(timer);
  }, [step, done, profile.needsMet]);

  return (
    <div className="screen">
      <LiveDemoTopBar now="Preparing demo" />
      <Stepper active={3} />
      <div className="prep">
        <div className="glow" />
        <div className="prep-inner">
          <div className="badge-lg">{session.badge}</div>
          <h1>Thank you{data.primaryParent ? `, ${data.primaryParent.split(" ").slice(-1)[0]}` : ""}.<br />Your child's <span className="accent">AI demo is being prepared.</span></h1>
          <p className="psub">{data.studentName || "Your child"}'s profile is complete. {session.school} is arranging the optimal sequence of AI teachers, counselling and podcasts for this conversation.</p>

          <div className="prep-tasks">
            {PREP_TASKS.map((task, index) => (
              <div key={task} className={"ptask " + (index < step ? "done" : index === step ? "run" : "pending")}>
                <span className="tick">{index === step && !done ? <span className="spinner" /> : <Icon name="check" />}</span>
                {task}
              </div>
            ))}
          </div>

          <div className="prep-meter">
            <div className="pm-top"><span>Needs met by current modules</span><span>{pct}%</span></div>
            <div className="pm-bar"><i style={{ width: `${pct}%` }} /></div>
          </div>

          <div className="prep-cta">
            <button className="btn btn-primary btn-lg" disabled={!done || openingDashboard} onClick={handleOpen}>
              {openingDashboard ? "Loading latest lead…" : done ? <>Open the live personalised demo <Icon name="arrow-right" /></> : "Preparing…"}
            </button>
          </div>
          <p className="prep-foot">Insights are shared with faculty and parents · NEP 2020 aligned</p>
        </div>
      </div>
    </div>
  );
}

export function DashboardHandoff({ data, onRestart }) {
  const session = useLiveDemoSession();

  return (
    <div className="screen">
      <div className="dash-stage">
        <div className="dash-frame"><img src="/admissions-assured/live-demo/dashboard.webp" alt="Admissions Assured - Counsellor Console" /></div>
      </div>
      <div className="dash-banner">
        <span className="ds-livedot" />
        <span>Live personalised demo ready for <b>{data.studentName || "the student"}</b> · {session.school}</span>
        <button onClick={onRestart}>Restart flow</button>
      </div>
    </div>
  );
}
